// =============================================================================
// src/chrome/search-palette/index.ts — SearchPalette: command palette global (Ctrl/Cmd+K).
//
// Proveniencia: porte FIEL de assets/unified-app.js `var SearchPalette` (2983-3217).
//   Overlay central com vocabulario real do produto (search-palette-items.ts):
//   filtragem por substring sobre `label + sub + group`, navegacao por setas,
//   Enter ativa o item focado. Sem backend.
//
// Timings/thresholds preservados (CONSTRAINT de paridade do usuario):
//   · foco do input 30ms apos abrir (legado 3084)
//   · ajuste de scroll do item focado: +/- 8px (legado 3168-3169)
//   · scroll-to-section apos navegar (delegado ao TopbarNav: 120ms)
//
// Acoplamento (sem global escondido): ativar um item delega ao TopbarNav TS
//   (go / scrollToSection) e ao MiniToast TS — substitui as chamadas diretas
//   TopbarNav.go(...)/MiniToast.show(...) do legado (3189-3209).
// =============================================================================

import type { AppContext } from '../../app/types';
import { byId, qs, qsa } from '../../app/dom';
import { TopbarNav } from '../topbar/index';
import { showMiniToast } from '../mini-toast/index';
import {
  SEARCH_PALETTE_ITEMS,
  type SearchPaletteItem,
} from './search-palette-items';
import { renderEmptyState, renderResults } from './search-palette-render';

/** Ms ate focar o input apos abrir (legado 3084). CONSTRAINT de paridade. */
const INPUT_FOCUS_DELAY_MS = 30;
/** Folga em px ao rolar o item focado para dentro da viewport (legado 3168-3169). */
const SCROLL_INTO_VIEW_PADDING_PX = 8;

/**
 * Command palette global. Singleton exportado ao final (SearchPalette), espelhando
 * o `var SearchPalette` legado. `init(ctx)` liga o atalho global e os listeners do
 * painel (markup vem de src/chrome/static-shell).
 */
class SearchPaletteController {
  private panel: HTMLElement | null = null;
  private input: HTMLInputElement | null = null;
  private results: HTMLElement | null = null;
  private trigger: HTMLElement | null = null;
  private focused = 0;
  private current: SearchPaletteItem[] = [];
  private isOpen = false;

  /**
   * Liga o botao de busca, o atalho Ctrl/Cmd+K e os listeners do painel
   * (teclado, backdrop, input, hover/click nos resultados). Replica
   * SearchPalette.init (3027-3072). No-op se o markup do painel nao existir.
   */
  init(_ctx: AppContext): void {
    this.panel = qs<HTMLElement>('#searchPalette');
    this.input = qs<HTMLInputElement>('#searchPaletteInput');
    this.results = qs<HTMLElement>('#searchPaletteResults');
    this.trigger = qs<HTMLElement>('#topbarSearch');
    if (!this.panel || !this.input || !this.results) return;
    this.bindTrigger();
    this.bindGlobalShortcut();
    this.bindPanelKeyboard();
    this.bindBackdropClose();
    this.bindInputAndResults();
  }

  /** Botao de busca da topbar abre a palette. */
  private bindTrigger(): void {
    this.trigger?.addEventListener('click', () => this.open());
  }

  /** Atalho global Ctrl/Cmd+K alterna a palette (replica 3041-3046). */
  private bindGlobalShortcut(): void {
    document.addEventListener('keydown', (event) => {
      if ((event.ctrlKey || event.metaKey) && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault();
        this.toggle();
      }
    });
  }

  /** Teclado dentro do painel: Esc fecha, setas navegam, Enter ativa (3049-3054). */
  private bindPanelKeyboard(): void {
    this.panel?.addEventListener('keydown', (event) => this.onPanelKeydown(event));
  }

  /** Mapeia a tecla do painel para a acao correspondente (early-return por tecla). */
  private onPanelKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.move(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.move(-1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.activate();
    }
  }

  /** Clique no backdrop ([data-close="palette"]) fecha (replica 3055-3057). */
  private bindBackdropClose(): void {
    this.panel?.addEventListener('click', (event) => {
      if ((event.target as HTMLElement).closest('[data-close="palette"]')) this.close();
    });
  }

  /** Liga o input (filtra) e os resultados (hover foca / click ativa) — 3059-3071. */
  private bindInputAndResults(): void {
    /* v8 ignore next -- input garantido não-nulo por init(); fallback ?? '' defensivo CLAUDE.md rule 11 */
    this.input?.addEventListener('input', () => this.render(this.input?.value ?? ''));
    this.results?.addEventListener('mouseover', (event) => this.onResultHover(event));
    this.results?.addEventListener('click', (event) => this.onResultClick(event));
  }

  /** Hover sobre um item foca-o (replica o mouseover legado). */
  private onResultHover(event: MouseEvent): void {
    const idx = resultRowIndex(event);
    if (idx !== null) this.setFocused(idx);
  }

  /** Click sobre um item foca-o e ativa (replica o click legado). */
  private onResultClick(event: MouseEvent): void {
    const idx = resultRowIndex(event);
    if (idx === null) return;
    this.setFocused(idx);
    this.activate();
  }

  /** Alterna aberto/fechado (replica toggle). */
  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  /** Abre a palette, limpa a busca, renderiza tudo e foca o input apos 30ms. */
  open(): void {
    if (!this.panel || !this.input) return;
    this.panel.setAttribute('aria-hidden', 'false');
    this.trigger?.classList.add('is-open');
    this.isOpen = true;
    this.input.value = '';
    this.render('');
    setTimeout(() => this.input?.focus(), INPUT_FOCUS_DELAY_MS);
  }

  /** Fecha a palette e limpa a busca (replica close). */
  close(): void {
    if (!this.panel || !this.input) return;
    this.panel.setAttribute('aria-hidden', 'true');
    this.trigger?.classList.remove('is-open');
    this.isOpen = false;
    this.input.value = '';
  }

  /**
   * Filtra por substring sobre `label + sub + group` (lowercase) e renderiza os
   * resultados agrupados. Replica SearchPalette.render (3095-3155).
   */
  render(rawQuery: string): void {
    if (!this.results) return;
    const query = rawQuery.trim().toLowerCase();
    this.current = filterItems(query);
    this.focused = 0;
    if (this.current.length === 0) {
      this.results.innerHTML = renderEmptyState(query);
      return;
    }
    this.results.innerHTML = renderResults(this.current, this.focused);
    this.setFocused(0);
  }

  /**
   * Marca o item `idx` como focado (clamp em [0, len-1]) e o rola para dentro da
   * viewport dos resultados com folga de 8px. Replica setFocused (3157-3174).
   */
  setFocused(idx: number): void {
    if (!this.results) return;
    this.focused = clampIndex(idx, this.current.length);
    qsa<HTMLElement>('.u-search-palette-item', this.results).forEach((row) => {
      this.applyRowFocus(row);
    });
  }

  /** Aplica o estado de foco a uma linha e a rola para a viewport se ativa. */
  private applyRowFocus(row: HTMLElement): void {
    const isOn = Number.parseInt(row.dataset.idx ?? '', 10) === this.focused;
    row.classList.toggle('is-focused', isOn);
    if (!isOn) {
      row.removeAttribute('aria-selected');
      return;
    }
    row.setAttribute('aria-selected', 'true');
    this.scrollRowIntoView(row);
  }

  /** Ajusta results.scrollTop em +/-8px para revelar o item focado (3166-3169). */
  private scrollRowIntoView(row: HTMLElement): void {
    /* v8 ignore next -- só chamado após setFocused validar this.results; guarda defensiva CLAUDE.md rule 11 */
    if (!this.results) return;
    const rowRect = row.getBoundingClientRect();
    const paneRect = this.results.getBoundingClientRect();
    if (rowRect.top < paneRect.top) {
      this.results.scrollTop -= paneRect.top - rowRect.top + SCROLL_INTO_VIEW_PADDING_PX;
    } else if (rowRect.bottom > paneRect.bottom) {
      this.results.scrollTop += rowRect.bottom - paneRect.bottom + SCROLL_INTO_VIEW_PADDING_PX;
    }
  }

  /** Move o foco por `delta` com wrap-around (replica move, 3176-3182). */
  move(delta: number): void {
    if (this.current.length === 0) return;
    let next = this.focused + delta;
    if (next < 0) next = this.current.length - 1;
    if (next >= this.current.length) next = 0;
    this.setFocused(next);
  }

  /** Ativa o item focado, fechando a palette antes de navegar (replica activate). */
  activate(): void {
    const item = this.current[this.focused];
    if (!item) return;
    this.close();
    this.dispatchItemAction(item);
  }

  /** Despacha a acao do item para o TopbarNav/MiniToast (replica 3189-3209). */
  private dispatchItemAction(item: SearchPaletteItem): void {
    if (item.action === 'section' && item.section) {
      TopbarNav.scrollToSection(item.section);
      return;
    }
    // Ramo 'workspace' REMOVIDO em 2026-07-25: o item "Workspace Pessoal" saiu da
    // lista junto com a desativacao da aba (nenhum item produz essa acao; ramo
    // morto seria branch descoberto no gate). Para religar: devolver o item em
    // search-palette-items.ts e o `if (item.action === 'workspace') TopbarNav.go(...)`.
    if (item.action === 'region') {
      TopbarNav.go('mapa');
      showMiniToast(`Foco em ${item.label}`, 'ti-map-pin');
      return;
    }
    if (item.action === 'partido') showMiniToast(`Filtrando por ${item.label}`, 'ti-filter');
  }
}

/** Filtra os itens por substring sobre `label + sub + group` (DRY com render). */
function filterItems(query: string): SearchPaletteItem[] {
  if (!query) return SEARCH_PALETTE_ITEMS.slice();
  return SEARCH_PALETTE_ITEMS.filter((item) => {
    const haystack = `${item.label} ${item.sub} ${item.group}`.toLowerCase();
    return haystack.includes(query);
  });
}

/** Le o data-idx da linha sob o evento; null se o clique nao foi numa linha. */
function resultRowIndex(event: MouseEvent): number | null {
  const row = (event.target as HTMLElement).closest<HTMLElement>('.u-search-palette-item');
  if (!row) return null;
  const idx = Number.parseInt(row.dataset.idx ?? '', 10);
  return Number.isNaN(idx) ? null : idx;
}

/** Clampa um indice em [0, length-1] (>= 0 sempre; len 0 vira 0, igual ao legado). */
function clampIndex(idx: number, length: number): number {
  return Math.max(0, Math.min(idx, length - 1));
}

/**
 * Singleton da command palette — substitui o `var SearchPalette` legado. main.ts
 * chama SearchPalette.init(ctx) apos montar o painel (static-shell) e a topbar.
 */
export const SearchPalette = new SearchPaletteController();
